import { getBoUrl } from './storage.ts';
import * as storage from './storage.ts';
import { checkApiRate, RateLimitError } from './bot-guard.ts';
export { RateLimitError } from './bot-guard.ts';

export interface SpaceItem {
  id: string;
  name: string;
  realmId: string;
  skillId: string | null;
  color: string; // '#RRGGBB'
}

/**
 * Snake-case geo_location object emitted by BO REST.
 * Spec: docs/requirements/capture-geo-location-v1.md §3.1.
 */
export interface CaptureGeoLocationApi {
  lat_e6: number;
  lng_e6: number;
  source: 'exif' | 'text' | 'map_picker' | 'manual' | 'device';
  accuracy_m?: number;
  altitude_m?: number;
  address?: string;
  place_name?: string;
  captured_at_ms?: number;
  map_app_picked?: 'osm_inline' | 'google_maps' | 'apple_maps' | 'amap';
}

/** camelCase variant the BO accepts on POST /v1/captures + PATCH /geo-location. */
export interface CaptureGeoLocationInput {
  latE6: number;
  lngE6: number;
  source: 'exif' | 'text' | 'map_picker' | 'manual' | 'device';
  accuracyM?: number;
  altitudeM?: number;
  address?: string;
  placeName?: string;
  capturedAtMs?: number;
  mapAppPicked?: 'osm_inline' | 'google_maps' | 'apple_maps' | 'amap';
}

export interface CaptureStub {
  capture_id: string;
  space: string;
  space_id?: string;
  realm_id?: string;
  summary: string;
  tags: string[];
  updated_at_ms: number;
  created_at_ms?: number;
  /** Capture geo location v1 — present on list/detail responses when set. */
  geo_location?: CaptureGeoLocationApi;
  /**
   * Capture provenance — `'app'` is the legacy mobile path; `'web'` comes
   * from this SPA's create flow (`docs/requirements/capture-source-web-v1.md`).
   * Used by the publish-state / Drafts filter
   * (`docs/requirements/fable-stories-publish-state-and-drafts-v1.md`) to
   * grandfather pre-spec `app`-source captures into the public plaza.
   */
  source?: 'app' | 'bot' | 'share' | 'web';
  // stub mode omits note_body_md
}

export interface CaptureDetail extends CaptureStub {
  note_body_md: string;
  text: string;
}

export interface CapturesPage {
  items: CaptureStub[];
  next_cursor: string | null;
  has_more: boolean;
  limit: number;
}

// ── BO session token (for /v1/* endpoints protected by tenant JWT) ─────────────

let _boToken: string | null = null;
let _boTokenFetchedAt = 0;
const BO_TOKEN_TTL_MS = 30 * 60 * 1000;

async function boAuthHeaders(): Promise<Record<string, string>> {
  const now = Date.now();
  if (!_boToken || now - _boTokenFetchedAt > BO_TOKEN_TTL_MS) {
    _boToken = await fetchBoToken();
    _boTokenFetchedAt = now;
  }
  return _boToken ? { Authorization: `Bearer ${_boToken}` } : {};
}

function fetchWithTimeout(input: string, init: RequestInit, timeoutMs = 8000): Promise<Response> {
  checkApiRate();
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), timeoutMs);
  return fetch(input, { ...init, signal: ac.signal }).finally(() => clearTimeout(timer));
}

function buildUrl(path: string, params?: Record<string, string>): string {
  const isDev = (import.meta.env.DEV as boolean);
  const boBase = getBoUrl().trim();
  const useRelative = !boBase;
  const url = isDev
    ? new URL(path, window.location.href)
    : (useRelative ? new URL(path, window.location.href) : new URL(`${boBase}${path}`));
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined && v !== '') url.searchParams.set(k, v);
    }
  }
  return url.toString();
}

async function fetchBoToken(): Promise<string | null> {
  // Same-origin same-disk BO; Nginx injects `X-CaptureX-Local-Profile-Secret`
  // server-side per
  // `docs/requirements/fable-stories-no-user-facing-backend-url-v1.md` §6.2,
  // so the SPA never sends the secret from the browser. Dev path goes through
  // the Vite proxy which forwards to the loopback BO (loopback whitelist
  // bypasses the secret check entirely).
  try {
    const res = await fetchWithTimeout(buildUrl('/auth/local/session'), {});
    if (!res.ok) return null;
    const data = await res.json() as { token?: string };
    return data.token ?? null;
  } catch {
    return null;
  }
}

export function invalidateToken(): void {
  _boToken = null;
  _boTokenFetchedAt = 0;
}

async function get<T>(path: string, params?: Record<string, string>): Promise<T> {
  const headers = await boAuthHeaders();
  const res = await fetchWithTimeout(buildUrl(path, params), { headers });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`BO ${res.status}: ${body.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

async function post<T>(path: string, body: unknown, params?: Record<string, string>): Promise<T> {
  const url = buildUrl(path, params);
  const headers = { ...(await boAuthHeaders()), 'Content-Type': 'application/json' };
  const res = await fetchWithTimeout(url, { method: 'POST', headers, body: JSON.stringify(body) }, 60000);
  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(`BO ${res.status}: ${errBody.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

// ── Fable auth token management (auto-refresh on expiry) ──────────────────────

// Hydrate the in-memory cache from localStorage at module init so a page
// refresh inside the access-token TTL (1 h) skips the refresh round-trip
// entirely. Without this, every cold start runs through the refresh path
// and a single transient network failure wipes the session — see
// LLM_OUTPUTS/2026/05/29/fable-stories-page-refresh-no-relogin.md.
let _fableAccessToken: string | null = storage.getFableAccessToken();
let _fableRefreshInFlight: Promise<string | null> | null = null;

/** Returns a valid fable access token, auto-refreshing if needed. */
async function getFableToken(): Promise<string | null> {
  // In-memory cache hit
  if (_fableAccessToken && !storage.isFableAccessTokenExpired()) return _fableAccessToken;

  // Cold-start / post-login fallback: in-memory cache is empty but the
  // localStorage token is still fresh (e.g. user just registered, or the
  // page was reloaded and the module-init hydrate raced an earlier
  // `setFableSession` from the modal handler).
  if (!_fableAccessToken && !storage.isFableAccessTokenExpired()) {
    const stored = storage.getFableAccessToken();
    if (stored) {
      _fableAccessToken = stored;
      return stored;
    }
  }

  // Token expired — try refresh
  const session = storage.getFableSession();
  if (!session) return null;

  // Deduplicate concurrent refresh calls
  if (_fableRefreshInFlight) return _fableRefreshInFlight;
  _fableRefreshInFlight = (async () => {
    try {
      const result = await fableRefreshToken(session.refreshToken);
      storage.setFableSession({
        nickname: session.nickname,
        accessToken: result.accessToken,
        refreshToken: session.refreshToken,
        expiresIn: result.expiresIn,
      });
      _fableAccessToken = result.accessToken;
      return result.accessToken;
    } catch (err) {
      // Only invalidate the session when BO *definitively* rejected the
      // refresh token (401 / 403 from `/v1/fable/auth/refresh` →
      // `UnauthorizedException` → SPA wraps as `BO 401:` / `BO 403:`).
      // Network errors / timeouts / BO startup races / 5xx must NOT wipe
      // the session — the refresh token is valid for 30 days and a single
      // transient hiccup should not force the user back to the login modal.
      const msg = err instanceof Error ? err.message : String(err);
      if (/^BO 40[13]:/.test(msg)) {
        storage.clearFableSession();
        _fableAccessToken = null;
      }
      return null;
    } finally {
      _fableRefreshInFlight = null;
    }
  })();
  return _fableRefreshInFlight;
}

export function invalidateFableToken(): void {
  _fableAccessToken = null;
}

// ── Fable HTTP helpers ─────────────────────────────────────────────────────────

/** GET with optional fable Bearer JWT (auto-refreshed). */
async function fableGet<T>(path: string, extraHeaders?: Record<string, string>): Promise<T> {
  const fableToken = await getFableToken();
  const headers: Record<string, string> = { ...(extraHeaders ?? {}) };
  if (fableToken) headers['Authorization'] = `Bearer ${fableToken}`;
  const res = await fetchWithTimeout(buildUrl(path), { headers });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`BO ${res.status}: ${body.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

/** POST without fable auth (public or auth endpoints). */
async function fablePostPublic<T>(path: string, body: unknown): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  const res = await fetchWithTimeout(buildUrl(path), { method: 'POST', headers, body: JSON.stringify(body) }, 15000);
  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(`BO ${res.status}: ${errBody.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

/** POST with fable Bearer JWT (auto-refreshed). */
async function fablePost<T>(path: string, body: unknown, extraHeaders?: Record<string, string>): Promise<T> {
  const fableToken = await getFableToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(extraHeaders ?? {}) };
  if (fableToken) headers['Authorization'] = `Bearer ${fableToken}`;
  const res = await fetchWithTimeout(buildUrl(path), { method: 'POST', headers, body: JSON.stringify(body) }, 15000);
  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(`BO ${res.status}: ${errBody.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

/** DELETE with fable Bearer JWT (auto-refreshed). */
async function fableDelete<T>(path: string, extraHeaders?: Record<string, string>): Promise<T> {
  const fableToken = await getFableToken();
  const headers: Record<string, string> = { ...(extraHeaders ?? {}) };
  if (fableToken) headers['Authorization'] = `Bearer ${fableToken}`;
  const res = await fetchWithTimeout(buildUrl(path), { method: 'DELETE', headers });
  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(`BO ${res.status}: ${errBody.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

/** PUT with fable Bearer JWT (auto-refreshed). */
async function fablePut<T>(path: string, body: unknown, extraHeaders?: Record<string, string>): Promise<T> {
  const fableToken = await getFableToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(extraHeaders ?? {}) };
  if (fableToken) headers['Authorization'] = `Bearer ${fableToken}`;
  const res = await fetchWithTimeout(buildUrl(path), { method: 'PUT', headers, body: JSON.stringify(body) }, 15000);
  if (!res.ok) {
    const errBody = await res.text().catch(() => '');
    throw new Error(`BO ${res.status}: ${errBody.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

// ── Public API ────────────────────────────────────────────────────────────────

export async function checkConnection(): Promise<boolean> {
  for (let attempt = 0; attempt < 3; attempt++) {
    if (attempt > 0) {
      await new Promise<void>((resolve) => setTimeout(resolve, 3000));
    }
    try {
      const token = await fetchBoToken();
      if (token) {
        _boToken = token;
        _boTokenFetchedAt = Date.now();
        return true;
      }
    } catch {
      // retry
    }
  }
  return false;
}

export async function listSpaces(): Promise<SpaceItem[]> {
  const data = await get<{ items: SpaceItem[] }>('/v1/spaces');
  return data.items;
}

export async function listCaptures(opts: {
  realmId: string;
  spaceId?: string;
  cursor?: string;
  limit?: number;
  include?: 'stub' | 'full';
}): Promise<CapturesPage> {
  const params: Record<string, string> = { realmId: opts.realmId };
  if (opts.spaceId) params.spaceId = opts.spaceId;
  if (opts.cursor) params.cursor = opts.cursor;
  if (opts.limit) params.limit = String(opts.limit);
  if (opts.include) params.include = opts.include;
  return get<CapturesPage>('/v1/captures', params);
}

export async function getCapture(captureId: string, realmId: string): Promise<CaptureDetail> {
  return get<CaptureDetail>(`/v1/captures/${captureId}`, { realmId });
}

export async function createCapture(opts: {
  realmId: string;
  spaceId: string;
  skillId: string;
  text: string;
  /** Optional capture geo location (iron rule #22 / capture-geo-location-v1). */
  geoLocation?: CaptureGeoLocationInput;
}): Promise<CaptureDetail> {
  const body: Record<string, unknown> = {
    text: opts.text,
    spaceId: opts.spaceId,
    skillId: opts.skillId,
    realmId: opts.realmId,
    source: 'web',
  };
  if (opts.geoLocation) body.geoLocation = opts.geoLocation;
  return post<CaptureDetail>('/v1/captures', body, { realmId: opts.realmId });
}

// ── Fable auth API ────────────────────────────────────────────────────────────

export interface FableAuthResult {
  user: { nickname: string; starsAvailable: number; createdAt: number };
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface FableRefreshResult {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

/**
 * Register a new fable user with nickname + password.
 * Phone is optional and used only as a personal memory aid — no SMS is sent.
 */
export async function fableRegister(opts: {
  nickname: string;
  password: string;
  phone?: string;
  inviteCode?: string;
  captchaId: string;
  captchaAnswer: string;
}): Promise<FableAuthResult> {
  return fablePostPublic<FableAuthResult>('/v1/fable/auth/register', opts);
}

/**
 * Whether registration on this BO is invite-gated. Read from the shared
 * `GET /v1/spa-config` (no auth). Fail-closed to `true` so a probe failure
 * still shows the invite field.
 * Spec: docs/requirements/fable-registration-invite-code-v1.md §5.
 */
export async function fableRegistrationInviteRequired(): Promise<boolean> {
  try {
    const res = await fetchWithTimeout(buildUrl('/v1/spa-config'), {}, 8000);
    if (!res.ok) return true;
    const cfg = (await res.json()) as { registrationInviteRequired?: boolean };
    return cfg.registrationInviteRequired !== false;
  } catch {
    return true;
  }
}

/** Login with nickname + password + captcha. Returns a JWT pair. */
export async function fableLogin(opts: {
  nickname: string;
  password: string;
  captchaId: string;
  captchaAnswer: string;
}): Promise<FableAuthResult> {
  return fablePostPublic<FableAuthResult>('/v1/fable/auth/login', opts);
}

/** Exchange a refresh token for a new access token. */
export async function fableRefreshToken(refreshToken: string): Promise<FableRefreshResult> {
  return fablePostPublic<FableRefreshResult>('/v1/fable/auth/refresh', { refreshToken });
}

// ── Fable account recovery + CAPTCHA ─────────────────────────────────────────

export interface FableCaptchaChallenge {
  id: string;
  /** `data:image/svg+xml;base64,…` */
  image: string;
  expiresAt: number;
}

/** Request a fresh CAPTCHA challenge. Server is rate-limited per IP. */
export async function fableIssueCaptcha(): Promise<FableCaptchaChallenge> {
  return fablePostPublic<FableCaptchaChallenge>('/v1/fable/auth/captcha', {});
}

/**
 * Phone → matching nicknames. Returns `{ matches: [] }` when nothing matches
 * (the BO never reveals "this phone has 0 vs N" — but the SPA should still
 * present "we listed all accounts linked to this phone" to avoid enumeration).
 */
export async function fableRecoverNickname(opts: {
  phone: string;
  captchaId: string;
  captchaAnswer: string;
}): Promise<{ matches: string[] }> {
  return fablePostPublic<{ matches: string[] }>('/v1/fable/auth/recover-nickname', opts);
}

/**
 * Reset the password for `nickname` when `phone` matches the stored phone
 * (digits-only equality). On 200 the caller should ask the user to sign in
 * with the new password.
 */
export async function fableResetPassword(opts: {
  nickname: string;
  phone: string;
  newPassword: string;
  captchaId: string;
  captchaAnswer: string;
}): Promise<{ nickname: string }> {
  return fablePostPublic<{ nickname: string }>('/v1/fable/auth/reset-password', opts);
}

// ── Fable engagement API ──────────────────────────────────────────────────────

export interface FableClaimResult {
  nickname: string;
  token: string;
  starsAvailable: number;
  createdAt: number;
}

export interface FableMeResult {
  nickname: string;
  starsAvailable: number;
  starsEarned: number;
  starsRedeemed: number;
  createdAt: number;
}

export interface FableLikeResult {
  liked: boolean;
  likeCount: number;
  starsAvailable: number;
  starEarned?: boolean;
}

export interface FableStoryStats {
  likeCount: number;
  isPublished: boolean;
  publishedAt?: number;
  tier?: string;
  publisherNickname?: string;
  /**
   * Per-capture authorship — set when the SPA user who created the
   * capture has claimed it via `POST /v1/fable/stories/:captureId/claim-author`
   * or publishStory has implicitly claimed it.
   * Absent for legacy / orphan rows. See
   * `docs/requirements/fable-stories-publish-state-and-drafts-v1.md` §4.3.
   */
  authorNickname?: string;
}

/** Compact bulk-stats wire shape — emitted by `POST /v1/fable/bulk-stats`. */
export interface FableBulkStatsRow {
  likeCount: number;
  isPublished: boolean;
  authorNickname?: string;
}

export interface FableAuthorshipResult {
  captureId: string;
  authorNickname: string;
  createdAt: number;
}

export interface FableDraftsListResult {
  drafts: string[];
}

export interface FablePublishResult {
  published: true;
  amountPaid: number;
  starsRedeemed: number;
  starsAvailable: number;
  publishedAt: number;
}

/** @deprecated Use fableRegister() for new accounts. */
export async function fableClaimNickname(nickname: string): Promise<FableClaimResult> {
  return fablePostPublic<FableClaimResult>('/v1/fable/users/claim', { nickname });
}

/** Fetch authenticated user profile + star balance. */
export async function fableGetMe(): Promise<FableMeResult> {
  return fableGet<FableMeResult>('/v1/fable/users/me');
}

/** Public: like count + publish status for one story. */
export async function fableGetStoryStats(captureId: string): Promise<FableStoryStats> {
  return fableGet<FableStoryStats>(`/v1/fable/stories/${encodeURIComponent(captureId)}/stats`);
}

/** Public: batch stats for up to 100 capture ids. */
export async function fableBulkStats(
  captureIds: string[],
): Promise<Record<string, FableBulkStatsRow>> {
  return fablePost('/v1/fable/bulk-stats', { captureIds });
}

/**
 * Public: BO-cached LLM-generated "Quick ideas" chip row for the create form.
 *
 * BO maintains a per-(spaceId) cache; only one LLM call is fired per refresh
 * window (default 5 min). Concurrent SPA visitors share the same cached list.
 * On LLM / network failure BO returns its hardcoded seed (`source = 'fallback'`),
 * and any thrown error here causes the caller to keep its own legacy chips.
 *
 * Spec: docs/requirements/fable-quick-ideas-llm-cache-v1.md.
 */
export interface FableQuickIdeasResult {
  items: string[];
  cachedAtMs: number;
  ttlMs: number;
  source: 'llm' | 'fallback';
  modelUsed: string | null;
}

export async function fableGetQuickIdeas(opts: {
  spaceId: string;
  spaceName?: string;
}): Promise<FableQuickIdeasResult> {
  const params: Record<string, string> = { spaceId: opts.spaceId };
  if (opts.spaceName) params.spaceName = opts.spaceName;
  // Same shape as bulkStats — public GET, no fable JWT required.
  const url = buildUrl('/v1/fable/quick-ideas', params);
  const res = await fetchWithTimeout(url, {}, 15000);
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`BO ${res.status}: ${body.slice(0, 200)}`);
  }
  return res.json() as Promise<FableQuickIdeasResult>;
}

/**
 * Claim authorship of a freshly-created capture so the SPA can place it
 * in the user's Drafts view AND hide it from the public plaza until the
 * user publishes it. First claim wins — subsequent claims by a different
 * nickname return 409 Conflict (handled by the caller as "ignored, the
 * row is already owned"). Idempotent for the same caller.
 *
 * Spec: docs/requirements/fable-stories-publish-state-and-drafts-v1.md §4.2.
 */
export async function fableClaimAuthorship(captureId: string): Promise<FableAuthorshipResult> {
  return fablePost<FableAuthorshipResult>(
    `/v1/fable/stories/${encodeURIComponent(captureId)}/claim-author`,
    {},
  );
}

/**
 * Returns the captureIds the authenticated user has authored but has NOT
 * yet published. The SPA cross-references the returned ids against the
 * realm-wide capture list cache to render the Drafts grid.
 *
 * Spec: docs/requirements/fable-stories-publish-state-and-drafts-v1.md §6.2.
 */
export async function fableListMyDrafts(): Promise<FableDraftsListResult> {
  return fableGet<FableDraftsListResult>('/v1/fable/users/me/drafts');
}

/** Check whether the authenticated user has an active like on this story. */
export async function fableGetMyLike(captureId: string): Promise<{ liked: boolean }> {
  return fableGet<{ liked: boolean }>(
    `/v1/fable/stories/${encodeURIComponent(captureId)}/my-like`,
  );
}

/** Like a published story. Returns updated like count + new star balance. */
export async function fableLikeStory(captureId: string): Promise<FableLikeResult> {
  return fablePost<FableLikeResult>(
    `/v1/fable/stories/${encodeURIComponent(captureId)}/like`,
    {},
  );
}

/** Unlike a published story. Star earned at first-like is kept. */
export async function fableUnlikeStory(captureId: string): Promise<FableLikeResult> {
  return fableDelete<FableLikeResult>(
    `/v1/fable/stories/${encodeURIComponent(captureId)}/like`,
  );
}

/**
 * Publish a story with optional star redemption.
 * 1 star = ¥0.01 discount; max 100 stars for community (¥1), 500 for supporter (¥5).
 * Test tier (¥0.01) disallows star redemption.
 */
export async function fablePublishStory(opts: {
  captureId: string;
  tier: string;
  starsToRedeem: number;
}): Promise<FablePublishResult> {
  return fablePost<FablePublishResult>(
    `/v1/fable/stories/${encodeURIComponent(opts.captureId)}/publish`,
    { tier: opts.tier, starsToRedeem: opts.starsToRedeem },
  );
}

// ── Fable collections (bookmarks) API ─────────────────────────────────────────
// Spec: docs/requirements/fable-stories-likes-and-collections-v1.md

export interface FableCollectionSummary {
  collectionId: string;
  name: string;
  itemCount: number;
  createdAt: number;
  updatedAt: number;
}

export interface FableCollectionsListResult {
  collections: FableCollectionSummary[];
}

export interface FableCollectionRow extends FableCollectionSummary {
  ownerNickname?: string;
}

export interface FableCollectionItemsResult {
  collectionId: string;
  name: string;
  items: Array<{ captureId: string; addedAt: number }>;
}

export interface FableBulkCollectionMembershipResult {
  membership: Record<string, string[]>;
}

export async function fableListMyCollections(): Promise<FableCollectionsListResult> {
  return fableGet<FableCollectionsListResult>('/v1/fable/users/me/collections');
}

export async function fableCreateCollection(name: string): Promise<FableCollectionRow> {
  return fablePost<FableCollectionRow>('/v1/fable/users/me/collections', { name });
}

export async function fableRenameCollection(
  collectionId: string,
  name: string,
): Promise<FableCollectionRow> {
  return fablePut<FableCollectionRow>(
    `/v1/fable/users/me/collections/${encodeURIComponent(collectionId)}`,
    { name },
  );
}

export async function fableDeleteCollection(
  collectionId: string,
): Promise<{ deleted: true; collectionId: string }> {
  return fableDelete<{ deleted: true; collectionId: string }>(
    `/v1/fable/users/me/collections/${encodeURIComponent(collectionId)}`,
  );
}

export async function fableListCollectionItems(
  collectionId: string,
): Promise<FableCollectionItemsResult> {
  return fableGet<FableCollectionItemsResult>(
    `/v1/fable/users/me/collections/${encodeURIComponent(collectionId)}/items`,
  );
}

export async function fableAddToCollection(
  collectionId: string,
  captureId: string,
): Promise<{ collectionId: string; captureId: string; addedAt: number }> {
  return fablePost(
    `/v1/fable/users/me/collections/${encodeURIComponent(collectionId)}/items`,
    { captureId },
  );
}

export async function fableRemoveFromCollection(
  collectionId: string,
  captureId: string,
): Promise<{ removed: true }> {
  return fableDelete<{ removed: true }>(
    `/v1/fable/users/me/collections/${encodeURIComponent(collectionId)}/items/${encodeURIComponent(captureId)}`,
  );
}

export async function fableBulkCollectionMembership(
  captureIds: string[],
): Promise<FableBulkCollectionMembershipResult> {
  return fablePost<FableBulkCollectionMembershipResult>(
    '/v1/fable/stories/bulk-collection-membership',
    { captureIds },
  );
}

// ── Animation requests ────────────────────────────────────────────────────────

export type AnimationStatus = 'none' | 'pending' | 'in_progress' | 'completed' | 'rejected';

export interface FableAnimationStatusResult {
  status: AnimationStatus;
  videoUrl?: string;
  thumbnailUrl?: string;
  description?: string;
  uploadedAt?: number;
}

export interface FableAnimationRequestResult {
  request: {
    captureId: string;
    requestedByNickname: string;
    requestedAt: number;
    status: AnimationStatus;
  };
  created: boolean;
}

/** Request animation generation for a story. Requires fable auth. */
export async function fableRequestAnimation(captureId: string): Promise<FableAnimationRequestResult> {
  return fablePost<FableAnimationRequestResult>(
    `/v1/fable/stories/${encodeURIComponent(captureId)}/animation-request`,
    {},
  );
}

/** Get animation status and result for a story. Public endpoint. */
export async function fableGetAnimationStatus(captureId: string): Promise<FableAnimationStatusResult> {
  return fableGet<FableAnimationStatusResult>(
    `/v1/fable/stories/${encodeURIComponent(captureId)}/animation`,
  );
}
